// src/components/auth/Turnstile.tsx
//
// La comprobación de Cloudflare Turnstile en el formulario de entrada.
//
// QUÉ HACE Y QUIÉN LO VERIFICA
//
// El widget entrega un token al navegador. Ese token NO se comprueba aquí:
// viaja con las credenciales a GoTrue, que lo valida contra Cloudflare desde
// el servidor. Un token comprobado en el navegador no prueba nada —quien
// controla el navegador controla la comprobación—.
//
// Por eso no hace falta escribir el `siteverify`: GoTrue v2.189.0 lo trae
// nativo. Basta con `GOTRUE_SECURITY_CAPTCHA_ENABLED=true`.
//
// SI CLOUDFLARE NO RESPONDE, SE ENTRA IGUAL
//
// Si el script no carga, se avisa y `onToken` recibe `null`: el formulario
// sigue funcionando. Dejar a un comerciante fuera de su propio sistema
// porque un captcha no cargó es peor que el riesgo que el captcha evita.
//
// OJO: esa red de seguridad es solo del lado del navegador. Si GoTrue tiene
// el captcha ACTIVO y el token no llega, el servidor rechaza igualmente. Por
// eso el interruptor real para una emergencia es `TURNSTILE_ENABLED=false`
// en el servidor, no este componente.

import React, { useEffect, useRef, useState } from 'react';

/**
 * La clave pública del widget.
 *
 * Va escrita aquí, no en una variable de entorno, PORQUE ES PÚBLICA: viaja
 * en el HTML y cualquiera la ve en el código fuente de la página. Lo secreto
 * es la otra clave, y esa vive solo en el servidor.
 *
 * Ponerla en `.env` obligaría a declararla como ARG y ENV en el Dockerfile y
 * a definirla en EasyPanel. Se intentó y el build de producción salió sin
 * ella: el widget no se montaba, el frontend no mandaba token y —con el
 * captcha ya exigido en el servidor— NADIE PODÍA ENTRAR.
 *
 * Una variable de entorno se justifica cuando el valor cambia por entorno o
 * es secreto. Esta no es ninguna de las dos cosas.
 *
 * El widget cubre: grooming-erp.com, www, el subdominio .cloud y localhost.
 */
const SITE_KEY = '0x4AAAAAAFDmhMJMyTELp-pb';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Cuánto se espera al reto antes de rendirse y dejar entrar igual.
 *
 * NO es un adorno. El widget puede cargar su script, montarse sin dar ningún
 * error y **quedarse colgado sin resolver nunca**: ni `error-callback` ni
 * `timeout-callback` se disparan. Se comprobó el 24/09/2026 contra producción
 * —el endpoint de attestation devolvía 401 y el reto no terminaba jamás—, y
 * el botón de entrar quedaba muerto para siempre, sin ninguna salida desde el
 * navegador.
 *
 * Con el captcha ya exigido en GoTrue, eso es un comerciante encerrado fuera
 * de su propio sistema por una red lenta o un reto que no cargó.
 *
 * 15 s es de sobra: resuelto normalmente tarda menos de 2.
 */
const PLAZO_MS = 15_000;

/** ¿Está activo? Lo usan Login y useAuth para no exigir un token que no habrá. */
export const turnstileActivo = Boolean(SITE_KEY);

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

/** Carga el script una sola vez aunque haya varios widgets en la página. */
let cargando: Promise<void> | null = null;
function cargarScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (cargando) return cargando;

  cargando = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { cargando = null; reject(new Error('No se pudo cargar Turnstile')); };
    document.head.appendChild(s);
  });
  return cargando;
}

interface Props {
  /** Recibe el token, o `null` si caducó, falló o no está configurado. */
  onToken: (token: string | null) => void;
  /** Para reiniciarlo tras un intento fallido: el token sirve una sola vez. */
  reiniciar?: number;
}

export const Turnstile: React.FC<Props> = ({ onToken, reiniciar = 0 }) => {
  const caja = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [fallo, setFallo] = useState(false);

  // `onToken` en una ref: si el padre la recrea en cada render, incluirla en
  // las dependencias volvería a montar el widget en cada pulsación de tecla.
  const alToken = useRef(onToken);
  alToken.current = onToken;

  useEffect(() => {
    if (!SITE_KEY) { alToken.current(null); return; }

    let vivo = true;
    let resuelto = false;

    // Si el reto no termina a tiempo, se deja pasar. Ver PLAZO_MS.
    const temporizador = window.setTimeout(() => {
      if (!vivo || resuelto) return;
      setFallo(true);
      alToken.current(null);
    }, PLAZO_MS);

    cargarScript()
      .then(() => {
        if (!vivo || !caja.current || !window.turnstile) return;

        widgetId.current = window.turnstile.render(caja.current, {
          sitekey: SITE_KEY,
          // El idioma se fija: el resto de la pantalla está en español.
          language: 'es',
          theme: 'light',
          callback: (token: string) => {
            resuelto = true;
            window.clearTimeout(temporizador);
            setFallo(false);
            alToken.current(token);
          },
          // Los tokens caducan a los 5 minutos. Si alguien deja el login
          // abierto y luego entra, el token ya no vale: se limpia para que el
          // formulario sepa que hay que resolverlo otra vez.
          'expired-callback': () => alToken.current(null),
          'error-callback': () => { setFallo(true); alToken.current(null); },
        });
      })
      .catch(() => {
        if (!vivo) return;
        // Cloudflare no responde. NO se bloquea la entrada: dejar a un
        // comerciante fuera de su propio sistema por un captcha caído es peor
        // que el riesgo que el captcha evita.
        setFallo(true);
        alToken.current(null);
      });

    return () => {
      vivo = false;
      window.clearTimeout(temporizador);
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* ya no estaba */ }
      }
    };
  }, []);

  // Reinicio tras un intento fallido: el token ya se gastó.
  useEffect(() => {
    if (reiniciar > 0 && widgetId.current && window.turnstile) {
      try { window.turnstile.reset(widgetId.current); } catch { /* ignorar */ }
      alToken.current(null);
    }
  }, [reiniciar]);

  if (!SITE_KEY) return null;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div ref={caja} />
      {fallo && (
        <p className="text-center text-xs text-muted-foreground">
          No se pudo completar la verificación. Intente entrar de todos modos;
          si no lo deja, recargue la página.
        </p>
      )}
    </div>
  );
};
